import * as React from "react";
import { Mesh, Program, Renderer, Triangle } from "ogl";
import { brandPlasma } from "../theme/tokens";

type PlasmaDirection = "forward" | "reverse" | "pingpong";

type PlasmaBackgroundProps = React.HTMLAttributes<HTMLDivElement> & {
  color?: string;
  speed?: number;
  direction?: PlasmaDirection;
  scale?: number;
  opacity?: number;
  mouseInteractive?: boolean;
  maxDpr?: number;
  targetFps?: number;
  iterations?: number;
  paused?: boolean;
};

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [1, 0.5, 0.2];
  return [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255,
  ];
};

const vertex = `#version 300 es
precision highp float;
in vec2 position;
in vec2 uv;
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragment = `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform vec3 uCustomColor;
uniform float uUseCustomColor;
uniform float uSpeed;
uniform float uDirection;
uniform float uScale;
uniform float uOpacity;
uniform vec2 uMouse;
uniform float uMouseInteractive;
out vec4 fragColor;

void mainImage(out vec4 o, vec2 C) {
  vec2 center = iResolution.xy * 0.5;
  C = (C - center) / uScale + center;

  vec2 mouseOffset = (uMouse - center) * 0.0002;
  C += mouseOffset * length(C - center) * step(0.5, uMouseInteractive);

  float i, d, z, T = iTime * uSpeed * uDirection;
  vec3 O, p, S;

  for (vec2 r = iResolution.xy, Q; ++i < __PLASMA_ITERATIONS__; O += o.w/d*o.xyz) {
    p = z*normalize(vec3(C-.5*r,r.y));
    p.z -= 4.;
    S = p;
    d = p.y-T;

    p.x += .4*(1.+p.y)*sin(d + p.x*0.1)*cos(.34*d + p.x*0.05);
    Q = p.xz *= mat2(cos(p.y+vec4(0,11,33,0)-T));
    z+= d = abs(sqrt(length(Q*Q)) - .25*(5.+S.y))/3.+8e-4;
    o = 1.+sin(S.y+p.z*.5+S.z-length(S-p)+vec4(2,1,0,8));
  }

  o.xyz = tanh(O/1e4);
}

bool finite1(float x){ return !(isnan(x) || isinf(x)); }
vec3 sanitize(vec3 c){
  return vec3(
    finite1(c.r) ? c.r : 0.0,
    finite1(c.g) ? c.g : 0.0,
    finite1(c.b) ? c.b : 0.0
  );
}

void main() {
  vec4 o = vec4(0.0);
  mainImage(o, gl_FragCoord.xy);
  vec3 rgb = sanitize(o.rgb);

  float intensity = (rgb.r + rgb.g + rgb.b) / 3.0;
  vec3 customColor = intensity * uCustomColor;
  vec3 finalColor = mix(rgb, customColor, step(0.5, uUseCustomColor));

  float alpha = length(rgb) * uOpacity;
  fragColor = vec4(finalColor, alpha);
}`;

const createFragment = (iterations: number) => {
  const safeIterations = Math.max(24, Math.min(60, Math.round(iterations)));
  return fragment.replace("__PLASMA_ITERATIONS__", safeIterations.toFixed(1));
};

export default function PlasmaBackground({
  color = brandPlasma.color,
  speed = 1,
  direction = "forward",
  scale = 1,
  opacity = 1,
  mouseInteractive = false,
  maxDpr = 1.25,
  targetFps = 60,
  iterations = 60,
  paused = false,
  style,
  ...props
}: PlasmaBackgroundProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const mousePos = React.useRef({ x: 0, y: 0 });
  const pausedRef = React.useRef(paused);
  const playbackRef = React.useRef<{
    start: () => void;
    stop: () => void;
    renderStill: () => void;
  } | null>(null);
  pausedRef.current = paused;

  React.useEffect(() => {
    pausedRef.current = paused;
    const playback = playbackRef.current;
    if (!playback) return;
    if (paused) {
      playback.stop();
      playback.renderStill();
      return;
    }
    playback.start();
  }, [paused]);

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    const containerEl = containerRef.current;

    const useCustomColor = color ? 1.0 : 0.0;
    const customColorRgb = color ? hexToRgb(color) : [1, 1, 1];
    const directionMultiplier = direction === "reverse" ? -1.0 : 1.0;
    const effectiveDpr = Math.max(0.25, Math.min(window.devicePixelRatio || 1, maxDpr));
    const effectiveFps = Math.max(1, Math.min(60, targetFps));
    const frameInterval = 1000 / effectiveFps;
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let renderer: Renderer;
    try {
      renderer = new Renderer({
        webgl: 2,
        alpha: true,
        antialias: false,
        dpr: effectiveDpr,
      });
    } catch {
      return undefined;
    }

    const gl = renderer.gl;
    if (!gl) return undefined;

    const canvas = gl.canvas;
    canvas.style.display = "block";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    containerEl.appendChild(canvas);

    const geometry = new Triangle(gl);
    const program = new Program(gl, {
      vertex,
      fragment: createFragment(iterations),
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Float32Array([1, 1]) },
        uCustomColor: { value: new Float32Array(customColorRgb) },
        uUseCustomColor: { value: useCustomColor },
        uSpeed: { value: speed * 0.4 },
        uDirection: { value: directionMultiplier },
        uScale: { value: scale },
        uOpacity: { value: opacity },
        uMouse: { value: new Float32Array([0, 0]) },
        uMouseInteractive: { value: mouseInteractive ? 1.0 : 0.0 },
      },
    });
    const mesh = new Mesh(gl, { geometry, program });

    const handleMouseMove = (event: MouseEvent) => {
      if (!mouseInteractive) return;
      const rect = containerEl.getBoundingClientRect();
      mousePos.current.x = event.clientX - rect.left;
      mousePos.current.y = event.clientY - rect.top;
      const mouseUniform = program.uniforms.uMouse.value as Float32Array;
      mouseUniform[0] = mousePos.current.x;
      mouseUniform[1] = mousePos.current.y;
    };

    if (mouseInteractive) {
      containerEl.addEventListener("mousemove", handleMouseMove);
    }

    const setSize = () => {
      const rect = containerEl.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      renderer.setSize(width, height);
      const res = program.uniforms.iResolution.value as Float32Array;
      res[0] = gl.drawingBufferWidth;
      res[1] = gl.drawingBufferHeight;
    };

    let raf = 0;
    let frameTimeout = 0;
    let resizeRaf = 0;
    let contextLost = false;
    let isVisible = true;
    let isDocumentVisible = typeof document === "undefined" || document.visibilityState !== "hidden";
    let lastFrameAt = 0;
    const startedAt = performance.now();
    const canAnimate = () =>
      !contextLost && isVisible && isDocumentVisible && !pausedRef.current && !prefersReducedMotion;

    const renderFrame = (frameTime: number) => {
      const timeValue = (frameTime - startedAt) * 0.001;

      if (direction === "pingpong") {
        const pingpongDuration = 10;
        const segmentTime = timeValue % pingpongDuration;
        const isForward = Math.floor(timeValue / pingpongDuration) % 2 === 0;
        const u = segmentTime / pingpongDuration;
        const smooth = u * u * (3 - 2 * u);
        const pingpongTime = isForward
          ? smooth * pingpongDuration
          : (1 - smooth) * pingpongDuration;
        program.uniforms.uDirection.value = 1.0;
        program.uniforms.iTime.value = pingpongTime;
      } else {
        program.uniforms.iTime.value = timeValue;
      }

      renderer.render({ scene: mesh });
    };

    const scheduleFrame = () => {
      if (!canAnimate() || raf || frameTimeout) return;
      const elapsed = performance.now() - lastFrameAt;
      const delay = Math.max(0, frameInterval - elapsed);
      frameTimeout = window.setTimeout(() => {
        frameTimeout = 0;
        if (canAnimate()) {
          raf = requestAnimationFrame(loop);
        }
      }, delay);
    };

    const loop = (frameTime: number) => {
      raf = 0;
      if (!canAnimate()) return;
      lastFrameAt = frameTime;
      renderFrame(frameTime);
      scheduleFrame();
    };

    const stopAnimation = () => {
      cancelAnimationFrame(raf);
      raf = 0;
      window.clearTimeout(frameTimeout);
      frameTimeout = 0;
    };

    const renderStill = () => {
      if (!contextLost && isVisible && isDocumentVisible) {
        renderFrame(performance.now());
      }
    };

    const startAnimation = () => {
      if (!canAnimate() || raf || frameTimeout) return;
      scheduleFrame();
    };

    const scheduleSize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;
        setSize();
        renderStill();
      });
    };

    const resizeObserver = new ResizeObserver(scheduleSize);
    resizeObserver.observe(containerEl);
    setSize();

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      contextLost = true;
      stopAnimation();
    };

    const handleContextRestored = () => {
      contextLost = false;
      if (isVisible) {
        stopAnimation();
        renderStill();
        startAnimation();
      }
    };

    canvas.addEventListener("webglcontextlost", handleContextLost);
    canvas.addEventListener("webglcontextrestored", handleContextRestored);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = isVisible;
        isVisible = entry.isIntersecting;
        if (!isVisible) {
          stopAnimation();
          return;
        }
        if (!wasVisible && !contextLost) {
          renderStill();
          startAnimation();
        }
      },
      { threshold: 0 },
    );
    intersectionObserver.observe(containerEl);

    const handleVisibilityChange = () => {
      isDocumentVisible = document.visibilityState !== "hidden";
      if (!isDocumentVisible) {
        stopAnimation();
        return;
      }
      if (!contextLost && isVisible) {
        renderStill();
        startAnimation();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    playbackRef.current = {
      start: () => {
        renderStill();
        startAnimation();
      },
      stop: stopAnimation,
      renderStill,
    };
    renderStill();
    startAnimation();

    return () => {
      playbackRef.current = null;
      stopAnimation();
      cancelAnimationFrame(resizeRaf);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      canvas.removeEventListener("webglcontextrestored", handleContextRestored);
      if (mouseInteractive) {
        containerEl.removeEventListener("mousemove", handleMouseMove);
      }
      try {
        containerEl.removeChild(canvas);
      } catch {
        // Canvas may already be detached during fast route changes.
      }
    };
  }, [color, direction, iterations, maxDpr, mouseInteractive, opacity, scale, speed, targetFps]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      {...props}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: mouseInteractive ? "auto" : "none",
        ...style,
      }}
    />
  );
}
